/** @jsxImportSource @emotion/react */
import Paper from '@mui/material/Paper';
import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import { Spinner, Tabs, SecondaryButton } from 'components';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'translation';
import { convertWeiToTokens, getContractAddress } from 'utilities';

import {
  useConvertVrt,
  useGetBalanceOf,
  useGetVrtConversionEndTime,
  useGetVrtConversionRatio,
  useGetXvsWithdrawableAmount,
  useWithdrawXvs,
  useRepayUmi, useGetMintedUmi, useGetUmiPrice,
  useGetClaimableRewards,
} from 'clients/api';
import { TOKENS } from 'constants/tokens';
import { AuthContext } from 'context/AuthContext';
import { VError } from 'errors/VError';
import dlVaultAbi from 'constants/contracts/abis/dlVault.json';
import DirectCallTest from './DirectCallTest';

import Convert, { ConvertProps } from './Convert';
import Withdraw, { WithdrawProps } from './Withdraw';
import { useStyles } from './styles';

// 直接调用 claimAndWithdraw 的测试接口
interface DirectClaimAndWithdrawInput {
  userSigner: ethers.Signer;
  amountWei: BigNumber;
  gasLimit?: string;
}

// 直接调用 claimAndWithdraw 函数
const directClaimAndWithdraw = async ({
  userSigner,
  amountWei,
  gasLimit = '500000',
}: DirectClaimAndWithdrawInput) => {
  try {
    const userAddress = await userSigner.getAddress();
    const dlVaultAddress = getContractAddress('dlVault');

    console.log('=== 直接调用 claimAndWithdraw ===');
    console.log('User:', userAddress);
    console.log('Amount:', amountWei.toString(), 'wei');
    console.log('Contract:', dlVaultAddress);

    // 创建合约实例
    const contract = new ethers.Contract(dlVaultAddress, dlVaultAbi, userSigner);

    // 调用 claimAndWithdraw
    const tx = await contract.claimAndWithdraw(amountWei.toFixed(0), { gasLimit });

    console.log('✅ 交易已发送:', tx.hash);

    // 等待确认
    const receipt = await tx.wait();

    console.log('✅ 交易已确认:', receipt.status === 1 ? '成功' : '失败');
    console.log('Gas 使用:', receipt.gasUsed.toString());
    console.log('区块号:', receipt.blockNumber);

    // 打印事件日志
    if (receipt.logs && receipt.logs.length > 0) {
      console.log('触发的事件:');
      receipt.logs.forEach((log: any, index: number) => {
        try {
          const parsedLog = contract.interface.parseLog(log);
          console.log(`  事件 ${index + 1}:`, parsedLog.name, parsedLog.args);
        } catch (e) {
          console.log(`  未解析事件 ${index + 1}:`, log);
        }
      });
    }

    return {
      success: true,
      txHash: tx.hash,
      receipt,
    };
  } catch (error) {
    console.error('❌ 调用失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export type ConvertVrtUiProps = ConvertProps & WithdrawProps & {
  onTestDirectCall: (amount: string) => Promise<any>;
  isTestLoading: boolean;
  account: any;
};

export const ConvertVrtUi = ({
  xvsToVrtConversionRatio,
  userVrtBalanceWei,
  convertVrtLoading,
  convertVrt,
  withdrawXvsLoading,
  withdrawXvs,
  xvsWithdrawableAmount,
  userMinte,
  pumice,
  onTestDirectCall,
  isTestLoading,
  account,
}: ConvertVrtUiProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [testAmount, setTestAmount] = useState('0.1');

  const handleTestClick = async () => {
    try {
      await onTestDirectCall(testAmount);
    } catch (error) {
      console.error('测试失败:', error);
    }
  };

  const tabsContent = [
    {
      title: t('convertVrt.convert'),
      content: (
        <Convert
          xvsToVrtConversionRatio={xvsToVrtConversionRatio}
          userVrtBalanceWei={userVrtBalanceWei}
          convertVrtLoading={convertVrtLoading}
          convertVrt={convertVrt}
          userMinte={userMinte}
          pumice={pumice}
        />
      ),
    },
      //     {
      //   title: '直接BNB手续费调用',
      //   content: <DirectCallTest account={account} />,
      // },
    {
      title: t('convertVrt.withdraw'),
      content: (
        <Withdraw
          xvsWithdrawableAmount={xvsWithdrawableAmount}
          withdrawXvsLoading={withdrawXvsLoading}
          withdrawXvs={withdrawXvs}
        />
      ),
    },
  ];

  return (
    <div css={[styles.root]}>
      <Paper css={styles.tabs}>
        <Tabs tabsContent={tabsContent} />
      </Paper>
    </div>
  );
};

const ConvertVrt = () => {
  const { account } = useContext(AuthContext);
  const accountAddress = account?.address;
  const [isTestLoading, setIsTestLoading] = useState(false);
  
  const { data: vrtConversionEndTimeData } = useGetVrtConversionEndTime();
  const { data: vrtConversionRatioData } = useGetVrtConversionRatio();
  const { data: userVrtBalanceData } = useGetBalanceOf(
    { accountAddress: accountAddress || '', token: TOKENS.vrt },
    { enabled: !!accountAddress },
  );

  // 添加DL代币余额查询
  const { data: userDlBalanceData } = useGetBalanceOf(
    { accountAddress: accountAddress || '', token: TOKENS.dl },
    { enabled: !!accountAddress },
  );

    // 获取可领取奖励
    const { data: claimableRewardsData, isLoading: isGetClaimableRewardsLoading } = useGetClaimableRewards(
      { accountAddress: accountAddress || '' },
      { enabled: !!accountAddress },
    );

  const { data: { totalWithdrawableAmount: xvsWithdrawableAmount } = {} } =
    useGetXvsWithdrawableAmount(
      { accountAddress: accountAddress || '' },
      { enabled: !!accountAddress },
    );

  const { data: userMintedUmiData, isLoading: isGetUserMintedUmiLoading } = useGetMintedUmi(
      {
        accountAddress: account?.address || '',
      },
      {
        enabled: !!account?.address,
      },
  );

  const { data: Price } = useGetUmiPrice(
      { accountAddress: accountAddress || '' },
      { enabled: !!accountAddress },
  );
  const { mutateAsync: convertVrt, isLoading: convertVrtLoading } = useConvertVrt();
  const { mutateAsync: withdrawXvs, isLoading: withdrawXvsLoading } = useWithdrawXvs();
  const { mutateAsync: umiControllerContract, isLoading: isumiControllerContract } = useRepayUmi();

  const handleConvertVrt = async (amount: string) => {
    if (!account) {
      throw new VError({ type: 'unexpected', code: 'walletNotConnected' });
    }

    const provider = new ethers.providers.Web3Provider(window.ethereum as any);
    const signer = provider.getSigner();

    return umiControllerContract({
      accountAddress: account.address,
      amountWei: new BigNumber(amount),
    });
  };

  const handleWithdrawXvs = async () => {
    if (!account) {
      throw new VError({ type: 'unexpected', code: 'walletNotConnected' });
    }

    const provider = new ethers.providers.Web3Provider(window.ethereum as any);
    const signer = provider.getSigner();

    return withdrawXvs({
      userSigner: signer,
    });
  };

  // 处理直接调用测试
  const handleTestDirectCall = async (amountInEther: string) => {
    if (!account) {
      throw new VError({ type: 'unexpected', code: 'walletNotConnected' });
    }

    setIsTestLoading(true);
    
    try {
      console.log('🧪 开始直接调用 claimAndWithdraw 测试');
      
      const provider = new ethers.providers.Web3Provider(window.ethereum as any);
      const signer = provider.getSigner();
      
      // 转换金额为 wei
      const amountWei = new BigNumber(amountInEther).multipliedBy(new BigNumber(10).pow(18));
      
      console.log(`测试提取 ${amountInEther} ETH (${amountWei.toString()} wei)`);
      
      const result = await directClaimAndWithdraw({
        userSigner: signer,
        amountWei,
        gasLimit: '500000',
      });
      
      if (result.success) {
        console.log('🎉 直接调用测试成功!');
        console.log('交易哈希:', result.txHash);
        alert(`✅ 直接调用测试成功!\n交易哈希: ${result.txHash}\n请查看控制台获取详细信息`);
      } else {
        console.error('❌ 直接调用测试失败:', result.error);
        alert(`❌ 直接调用测试失败: ${result.error}`);
      }
      
      return result;
    } catch (error) {
      console.error('测试执行失败:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`测试执行失败: ${errorMessage}`);
      throw error;
    } finally {
      setIsTestLoading(false);
    }
  };

  const conversionRatio = useMemo(() => {
    if (vrtConversionRatioData?.conversionRatio) {
      return convertWeiToTokens({
        valueWei: new BigNumber(vrtConversionRatioData.conversionRatio),
        token: TOKENS.hktlp,
      });
    }

    return undefined;
  }, [vrtConversionRatioData?.conversionRatio]);

  return (
      <ConvertVrtUi
        xvsToVrtConversionRatio={conversionRatio}
        userVrtBalanceWei={userDlBalanceData?.balanceWei || userVrtBalanceData?.balanceWei}
        convertVrtLoading={isumiControllerContract}
        convertVrt={handleConvertVrt}
        withdrawXvs={handleWithdrawXvs}
        withdrawXvsLoading={withdrawXvsLoading}
        xvsWithdrawableAmount={claimableRewardsData?.claimableRewards}
        userMinte={userMintedUmiData}
        pumice={Price?.getPoolPrice}
        onTestDirectCall={handleTestDirectCall}
        isTestLoading={isTestLoading}
        account={account}
      />
  );

  // @TODO - Handle error state
  return <Spinner />;
};

export default ConvertVrt;
